import * as sentryLib from "@sentry/react";

import { TRPCClientError } from "@trpc/client";
import userCache from "cache/userCache";
import { extractUrlRegion, redactCredentialQueryParams, redactCredentialUrlsInRecordingEvent, redactCredentialUrlsInReplayEvent, redactUlidsFromPath } from "reusable/utils/url-utils";
import { createSentryEventThrottle } from "@gap/utils/sentry/sentryEventThrottle";
import { Timeouts } from "@gap/utils/date-utils";
import variables from "variables/variables";
import { canonicalStage } from "@gap/utils/stage";
import { DEPLOY_SKEW_TAG, isChunkLoadError } from "reusable/app-version/chunkLoadError";

interface SentryUser extends sentryLib.User {
   sessionExpires?: string
}

export class Sentry {
   static isEnabled = !!import.meta.env.VITE_SENTRY_DSN

   static contexts = {
      org: 'Org',
   } as const

   static tags = {
      appRegion: 'appRegion',
      isTrpcClientError: 'isTrpcClientError',
   } as const

   private static _user: SentryUser | null = null
   private static _org: OrgProperties | null = null

   /**
    * Caps how many of the same error we forward per hour, so a runaway loop
    * (e.g. a stuck stream retrying every few seconds in a long-lived tab) can't
    * drain the Sentry quota. The first few occurrences still go through, and
    * distinct errors are unaffected since each keeps its own per-fingerprint
    * budget. An hour-long window is deliberate: a tab firing every ~10s would
    * only be cut ~17% by a 60s window, but ~99% by this one.
    */
   private static shouldSendEvent = createSentryEventThrottle({
      windowMs: Timeouts.hour,
      maxEventsPerWindow: 5,
   })

   /**
    * Internal Gap staff, identified by their `@thegaphq.com` email (the same
    * domain the feature-toggle bypass and the OrgGuard `isGap*` getters key off).
    * We deliberately keep full fidelity in Sentry for these users — no event
    * throttling and no ULID redaction in the transaction — so our own sessions
    * surface every occurrence with the real entity IDs intact, at the cost of a
    * little extra quota. The email is set via `setUserProperties` before any
    * error is reported; if it's missing we treat the user as non-Gap and scrub.
    */
   private static get isGapUser() {
      return this._user?.email?.endsWith(`@${variables.gapDomain}`) ?? false
   }

   static setUserProperties(userProperties: SentryUser) {
      if (!this.isEnabled) return

      // Allows us to progressively add
      this._user = {
         ...(this._user || {}),
         ...userProperties,
      }

      sentryLib.setUser(this._user)
   }

   static reportCustomMessage(name: string, message: Record<string, any>) {
      sentryLib.captureMessage(name, {
         extra: message
      })
   }

   static setOrgProperties(orgProperties: OrgProperties) {
      this._org = {
         ...(this._org || {}),
         ...orgProperties
      }

      sentryLib.setContext(this.contexts.org, { ...orgProperties })
   }

   static unsetUser() {
      if (!this.isEnabled) return

      this._user = null
      sentryLib.setUser(this._user)

      this._org = null
      sentryLib.setContext(this.contexts.org, this._org)
   }

   static initialiseIfEnabled() {
      if (!this.isEnabled) return

      const replayMaskingConfig: {
         maskAllText: boolean;
         maskAllInputs: boolean;
         blockAllMedia: boolean;
         mask?: string[];
      } = {
         maskAllText: false,
         maskAllInputs: false,
         blockAllMedia: false,
         mask: ['input[type="password"]', '[data-sensitive="true"]']
      }


      sentryLib.init({
         dsn: import.meta.env.VITE_SENTRY_DSN,
         environment: canonicalStage(variables.stage),
         release: import.meta.env.VITE_SENTRY_RELEASE,
         integrations: [
            sentryLib.browserTracingIntegration(),
            ...userCache.serviceUser.get() ? [] : [
               sentryLib.replayIntegration({
                  ...replayMaskingConfig,
                  beforeAddRecordingEvent: (event) => redactCredentialUrlsInRecordingEvent(event),
               })
            ]
         ],
         enableLogs: true,
         // Performance Monitoring
         // NO-442: disabled — span/transaction ingestion was driving a monthly quota overage.
         tracesSampleRate: 0,
         // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
         tracePropagationTargets: [new RegExp(`^${variables.apiUrl}/(?!mix/).*`, 'i')],
         // Session Replay
         replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
         // Capture an error replay for 20% of errored sessions on prod, and none elsewhere,
         // so on-error replays can't drain the shared Sentry quota (replays aren't covered by spike protection).
         replaysOnErrorSampleRate: canonicalStage(variables.stage) === 'prod' ? 0.2 : 0,
         beforeSend: (event, { originalException }) => {
            const isGapUser = this.isGapUser
            const isChunkLoadFailure = isChunkLoadError(originalException)

            // Gap staff and chunk-load failures bypass the per-fingerprint cap.
            if (!isGapUser && !isChunkLoadFailure && !this.shouldSendEvent(event)) return null

            // Process the current URL to group events logically. Keep the real
            // entity IDs in the transaction for Gap staff; redact ULIDs otherwise.
            const { path, region } = extractUrlRegion(window.location)
            event.transaction = isGapUser ? path : redactUlidsFromPath(path)
            event.tags = {
               ...event.tags,
               [this.tags.appRegion]: region,
               ...(isChunkLoadFailure && { [DEPLOY_SKEW_TAG]: 'chunk' }),
            }

            /**
            * If this is a TRPCClientError, reverse the stack trace so that Sentry identifies
            * it by the original error
            */
            if (originalException instanceof TRPCClientError && originalException.cause instanceof Error && event.exception?.values?.length && event.exception?.values?.length >= 2) {
               const [trpcClientError, ...actualErrors] = event.exception.values
               event.exception.values = [...actualErrors, trpcClientError]
               event.tags[this.tags.isTrpcClientError] = true
            }

            // Backstop for credentials in the address bar: `httpContextIntegration` attaches the
            // full href to every event, so anything the OAuth route did not strip is redacted here.
            if (event.request?.url) {
               event.request = { ...event.request, url: redactCredentialQueryParams(event.request.url) }
            }
            event.breadcrumbs = event.breadcrumbs?.map(breadcrumb => {
               const { from, to, url } = breadcrumb.data ?? {}
               if (typeof from !== 'string' && typeof to !== 'string' && typeof url !== 'string') return breadcrumb
               return {
                  ...breadcrumb,
                  data: {
                     ...breadcrumb.data,
                     ...(typeof from === 'string' && { from: redactCredentialQueryParams(from) }),
                     ...(typeof to === 'string' && { to: redactCredentialQueryParams(to) }),
                     ...(typeof url === 'string' && { url: redactCredentialQueryParams(url) }),
                  },
               }
            })

            return event
         }
      })

      registerReplayUrlScrub(sentryLib.getClient())
   }
}

/** The one client capability this needs, so a test can hand it a plain object. */
interface ReplayScrubClient {
   on: (hook: 'preprocessEvent', handler: (event: { type?: string }) => void) => unknown
}

/**
 * Scrubs the replay event's own `urls` list, which neither `beforeAddRecordingEvent` nor
 * `beforeSend` sees. `preprocessEvent` is the hook that does, since the replay integration sends its
 * envelope through `transport.send` directly. Exported for test.
 */
export function registerReplayUrlScrub(client: ReplayScrubClient | undefined): void {
   client?.on('preprocessEvent', (event: { type?: string }) => {
      if (event.type !== 'replay_event') return
      redactCredentialUrlsInReplayEvent(event as { urls?: unknown; request?: { url?: unknown } })
   })
}

interface OrgProperties {
   id?: string
   name?: string
}
